import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, Typography, Row, Col, Tag, Checkbox, Button, EmptyState, Space, Alert, Input, Tooltip, message,
  List, Modal, theme,
} from '../../ui';
import type { TagTone } from '../../ui';
import { Save, Search } from 'lucide-react';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';

const ROLE_COLOR: Record<string, TagTone> = {
  SUPER_ADMIN: 'red', ADMIN: 'red', ACCOUNTS: 'gold',
  SALES_HEAD: 'purple', SALES_PERSON: 'blue', RETAILER: 'cyan', FARMER: 'green',
};

export function RolesPage() {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const [activeRoleId, setActiveRoleId] = useState<string | undefined>();
  const [pickedCodes, setPickedCodes] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  // Only SUPER_ADMIN may edit the permission matrix; everyone else is read-only.
  const viewerIsSuper = useAuthStore((s) => s.user?.role) === 'SUPER_ADMIN';

  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles-with-perms'],
    queryFn: () => request<any[]>({ url: '/roles' }),
  });
  const { data: permissions } = useQuery({
    queryKey: ['permissions-all'],
    queryFn: () => request<any[]>({ url: '/permissions' }),
  });

  const activeRole = roles?.find((r: any) => String(r.id) === activeRoleId);

  const seededRoleRef = useRef<string | null | undefined>(null);
  useEffect(() => {
    if (!activeRole) return;
    // Seed the picker once per role; a background refetch (same role id) must NOT
    // overwrite the user's unsaved permission edits.
    if (seededRoleRef.current === activeRoleId) return;
    seededRoleRef.current = activeRoleId;
    setPickedCodes(new Set((activeRole.permissions ?? []).map((p: any) => p.code)));
  }, [activeRole, activeRoleId]);

  const grouped = useMemo(() => {
    const byModule: Record<string, any[]> = {};
    for (const p of permissions ?? []) (byModule[p.module] ??= []).push(p);
    return byModule;
  }, [permissions]);

  const save = useMutation({
    mutationFn: () =>
      request({
        url: `/roles/${activeRoleId}/permissions`,
        method: 'PUT',
        data: { permissionCodes: Array.from(pickedCodes) },
      }),
    onSuccess: () => {
      message.success('Permissions saved');
      qc.invalidateQueries({ queryKey: ['roles-with-perms'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const toggle = (code: string, on: boolean) => {
    const next = new Set(pickedCodes);
    if (on) next.add(code); else next.delete(code);
    setPickedCodes(next);
  };
  const togglePerms = (perms: any[], on: boolean) => {
    const next = new Set(pickedCodes);
    perms.forEach((p) => { if (on) next.add(p.code); else next.delete(p.code); });
    setPickedCodes(next);
  };

  const isSuper = activeRole?.code === 'SUPER_ADMIN';
  const canEdit = viewerIsSuper && !isSuper;

  const savedCodes = useMemo(
    () => new Set((activeRole?.permissions ?? []).map((p: any) => p.code)),
    [activeRole],
  );
  const dirty = canEdit && (pickedCodes.size !== savedCodes.size || [...pickedCodes].some((c) => !savedCodes.has(c)));
  const selectRole = (id: string) => {
    if (id === activeRoleId) return;
    if (dirty) {
      Modal.confirm({
        title: 'Discard unsaved changes?',
        content: 'You have unsaved permission changes for this role. Switch and discard them?',
        okText: 'Discard',
        okButtonProps: { danger: true },
        onOk: () => setActiveRoleId(id),
      });
    } else {
      setActiveRoleId(id);
    }
  };

  const q = search.trim().toLowerCase();
  const matches = (p: any) =>
    !q || p.code.toLowerCase().includes(q) || (p.action ?? '').toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q);

  // Modules with at least one matching permission, sorted for a tidy grid.
  const modules = Object.entries(grouped)
    .map(([mod, perms]) => [mod, (perms as any[]).filter(matches)] as [string, any[]])
    .filter(([, shown]) => shown.length > 0)
    .sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div>
      <Typography.Title level={4}>Roles &amp; Permissions</Typography.Title>

      {/* align="stretch" makes the left column span the full row height so the
          sticky Roles card has room to stay pinned while the right side scrolls. */}
      <Row gutter={16} className="items-stretch">
        <Col xs={24} md={7} lg={6}>
          <Card title="Roles" className="sticky top-19">
            <List
              loading={isLoading}
              dataSource={roles ?? []}
              renderItem={(r: any) => {
                const active = activeRoleId === String(r.id);
                return (
                  <List.Item
                    onClick={() => selectRole(String(r.id))}
                    style={{
                      cursor: 'pointer',
                      background: active ? token.colorPrimaryBg : undefined,
                      padding: '10px 10px',
                      borderRadius: 8,
                      border: `1px solid ${active ? token.colorPrimaryBorder : 'transparent'}`,
                      marginBottom: 6,
                    }}
                  >
                    <List.Item.Meta
                      title={
                        <Space size={6}>
                          <Tag tone={ROLE_COLOR[r.code] ?? 'neutral'}>{r.code}</Tag>
                          {!!r.isSystem && <Tag>System</Tag>}
                        </Space>
                      }
                      description={
                        <div>
                          <div>{r.name}</div>
                          <div style={{ fontSize: 11, color: token.colorTextTertiary }}>
                            {r.code === 'SUPER_ADMIN' ? 'All permissions' : `${(r.permissions ?? []).length} permissions`}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>

        <Col xs={24} md={17} lg={18}>
          <Card
            title={activeRole ? `Permissions · ${activeRole.code}` : 'Pick a role on the left'}
            extra={activeRole && canEdit && (
              <Button variant="primary" icon={<Save size={16} />} loading={save.isPending} onClick={() => save.mutate()}>
                Save ({pickedCodes.size})
              </Button>
            )}
          >
            {!activeRole && <EmptyState title="Select a role to view its permissions" />}

            {activeRole && !isSuper && !viewerIsSuper && (
              <Alert type="info" className="mb-3"
                title="Read-only"
                description="Only the Super Admin can edit role permissions. You can review the current assignments below." />
            )}
            {isSuper && (
              <Alert type="warning"
                title="SUPER_ADMIN has wildcard access"
                description="This role always has every permission (server-side bypass). The checklist is informational." />
            )}

            {activeRole && !isSuper && (
              <>
                <Input
                  prefix={<Search size={16} />}
                  placeholder="Search permissions…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mb-3.5 max-w-90"
                />
                {modules.length === 0 ? (
                  <EmptyState title="No permissions match your search" />
                ) : (
                  // Masonry: two roomy columns on a normal screen (one on mobile)
                  // — compact but readable, not cramped.
                  <div style={{ columnWidth: 440, columnGap: 16 }}>
                    {modules.map(([mod, shown]) => {
                      const onCount = shown.filter((p) => pickedCodes.has(p.code)).length;
                      const allOn = onCount === shown.length;
                      return (
                        <div
                          key={mod}
                          style={{
                            breakInside: 'avoid',
                            marginBottom: 14,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: token.colorBgContainer,
                          }}
                        >
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '7px 12px', background: token.colorFillQuaternary,
                            borderBottom: `1px solid ${token.colorBorderSecondary}`,
                          }}>
                            <Space size={8}>
                              <b style={{ fontSize: 13 }}>{mod}</b>
                              <Tag
                                tone={allOn ? 'green' : onCount > 0 ? 'gold' : 'neutral'}
                                className="text-[11px] leading-4"
                              >
                                {onCount}/{shown.length}
                              </Tag>
                            </Space>
                            {canEdit && (
                              <Space size={0}>
                                <Button variant="text" size="sm" style={{ padding: '0 6px' }} onClick={() => togglePerms(shown, true)}>All</Button>
                                <Button variant="soft-danger" size="sm" style={{ padding: '0 6px' }} onClick={() => togglePerms(shown, false)}>None</Button>
                              </Space>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 16, rowGap: 6, padding: '8px 12px 10px' }}>
                            {shown.map((p) => {
                              // Label = code without the module prefix, so two perms that
                              // share an action (EXPENSE.APPROVE.ACCT / .HEAD) stay distinct.
                              const label = p.code.includes('.') ? p.code.slice(p.code.indexOf('.') + 1) : p.code;
                              return (
                                <Tooltip key={p.code} label={p.description ? `${p.code} — ${p.description}` : p.code} side="top">
                                  <Checkbox
                                    checked={pickedCodes.has(p.code)}
                                    disabled={!canEdit}
                                    onCheckedChange={(checked) => toggle(p.code, checked)}
                                    label={<span style={{ fontSize: 13 }}>{label}</span>}
                                  />
                                </Tooltip>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
